/* eslint-disable arrow-body-style */
/* eslint-disable consistent-return */
/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useState } from 'react';

import { Input as HeroInput, Tooltip } from '@heroui/react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  FaAddressCard,
  FaCopy,
  FaEnvelope,
  FaEye,
  FaEyeSlash,
  FaLock,
  FaQuestion,
  FaSearch,
  FaShareAlt,
  FaUser,
} from 'react-icons/fa';
import { HiMiniMapPin } from 'react-icons/hi2';

import './input.css';

import { InputProps } from './interface/interface';

export const HInput = ({
  Component,
  icon,
  icon2,
  errorMessage,
  label,
  placeholder,
  register,
  type,
  width = '',
  tooltip,
  theme = 'light',
  onCopy,
  height = 'regular',
  onChange = () => {},
  onBlur = () => {},
  value,
  name,
  onKeyDown = () => {},
  onFocus = () => {},
  maxLength,
}: InputProps) => {
  const [isPassword, setIsPassword] = useState(false);
  const [currentPlaceholderIndex, setCurrentPlaceholderIndex] = useState(0);
  const [inputValue, setInputValue] = useState(value || '');
  const isPlaceholderArray = Array.isArray(placeholder);
  const displayPlaceholder = isPlaceholderArray ? placeholder[currentPlaceholderIndex] : placeholder;

  let inputName = name;
  if (!inputName) {
    if (typeof label === 'string') {
      inputName = label;
    } else {
      inputName = 'input';
    }
  }
  const handleCopyToClipboard = () => {
    onCopy?.();
  };

  const IconComponent = (() => {
    switch (icon) {
      case 'user':
        return <FaUser className={`text-icon--${theme}`} />;
      case 'lock':
        return <FaLock className={`text-icon--${theme}`} />;
      case 'mail':
        return <FaEnvelope className={`text-icon--${theme}`} />;
      case 'search':
        return <FaSearch className={`text-icon--${theme}`} />;
      case 'share':
        return <FaShareAlt className={`text-icon--${theme}`} />;
      case 'name':
        return <FaUser className={`text-icon--${theme}`} />;
      case 'lastName':
        return <FaUser className={`text-icon--${theme}`} />;
      case 'dni':
        return <FaAddressCard className={`text-icon--${theme}`} />;
      case 'department':
        return <HiMiniMapPin className={`text-icon--${theme}`} />;
      case 'address':
        return <HiMiniMapPin className={`text-icon--${theme}`} />;
      default:
        return null;
    }
  })();

  const IconComponent2 = (() => {
    switch (icon2) {
      case 'eye':
        return (
          <button
            className="relative h-5"
            type="button"
            onClick={() => { return setIsPassword(!isPassword); }}
          >
            {isPassword ? (
              <FaEyeSlash className={`text-icon--${theme}`} />
            ) : (
              <FaEye className={`text-icon--${theme}`} />
            )}
          </button>
        );
      case 'question':
        return (
          <Tooltip
            showArrow
            classNames={{
              base: ['before:bg-neutral-400 dark:before:bg-white'],
              content: [
                'py-2 px-4 shadow-xl',
                'text-black bg-gradient-to-br from-white to-neutral-400',
              ],
            }}
            content={tooltip || 'I am a tooltip'}
            placement="right"
          >
            <button
              className="relative h-6 w-6 rounded-full bg-default-900 p-1"
              type="button"
            >
              <FaQuestion
                className={`text-icon--${theme} relative top-1/2 right-0 transform -translate-y-1/2`}
                data-testid="question-icon"
              />
            </button>
          </Tooltip>
        );
      case 'copy':
        return (
          <button
            type="button"
            className="relative h-6 w-6 rounded-full bg-white p-1"
            onClick={handleCopyToClipboard}
          >
            <FaCopy className={`text-icon--${theme}`} />
          </button>
        );
      default:
        return null;
    }
  })();

  const handleCopy = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
  };

  useEffect(() => {
    if (!isPlaceholderArray || !placeholder || placeholder.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentPlaceholderIndex((prev) => (prev + 1) % placeholder.length);
    }, 3000);

    return () => clearInterval(interval);
  }, [isPlaceholderArray, placeholder]);

  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  const registerOnChange = register?.onChange;
  const registerOnBlur = register?.onBlur;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    registerOnChange?.(e);
    onChange(e);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    registerOnBlur?.(e);
    onBlur(e);
  };

  const inputClassNames = {
    input: [
      `input input--${height} w-full input--${theme} ${errorMessage ? `input-wrapper--error--${theme}` : ''}`,
      '!bg-transparent',
      'data-[focus=true]:!bg-transparent',
      'data-[hover=true]:!bg-transparent',
      'focus:!bg-transparent',
      'hover:!bg-transparent',
      'focus-within:!bg-transparent',
      'data-[invalid=true]:!bg-default-900',
      'data-[invalid=true]:!border-danger-500',
      'autofill:!bg-transparent',
      'autofill:!shadow-none',
      'autofill:!text-inherit',
    ],
    innerWrapper: [
      'bg-transparent',
      '!bg-transparent',
      'data-[focus=true]:!bg-transparent',
      'data-[hover=true]:!bg-transparent',
      'focus-within:!bg-transparent',
      `input-wrapper--${height}`,
      'autofill:!bg-transparent',
      'autofill:!shadow-none',
      'autofill:!border-transparent',
      'autofill:!outline-none',
      'min-h-0',
      '!min-h-0',
      'h-auto',
      '!h-auto',
      '!bg-transparent',
      'data-[hover=true]:!bg-transparent',
      'data-[focus=true]:!bg-transparent',
      'group-data-[focus=true]:!bg-transparent',
    ],
    inputWrapper: [
      `input-wrapper w-full rounded-xl input-wrapper--${theme} input-wrapper--${height}`,
      'data-[focus=true]:!border-hprimary-500',
      'data-[hover=true]:!border-hprimary-400',
      'focus-within:!border-hprimary-500',
      `${errorMessage ? `input-wrapper--error--${theme}` : ''}`,
      'autofill:!bg-transparent',
      'autofill:!shadow-none',
      'autofill:!border-inherit',
      'autofill:!text-inherit',
      'min-h-0',
      '!min-h-0',
      'h-auto',
      '!h-auto',
    ],
    label: [`custom-label--${theme}`],
  };

  const placeholderContent = isPlaceholderArray ? (
    <AnimatePresence mode="wait">
      <motion.span
        key={currentPlaceholderIndex}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
        className="inline-block"
      >
        {displayPlaceholder}
      </motion.span>
    </AnimatePresence>
  ) : (
    displayPlaceholder
  );

  return (
    <div className={`flex flex-col text-sm font-text ${width}`}>
      {label && (
        <label
          htmlFor={inputName}
          className={`mb-1 text-sm font-medium label--${theme}`}
        >
          {label}
        </label>
      )}
      <div className="flex items-center gap-1 w-full bg-transparent">
        {Component}
        <div className="relative w-full">
          <HeroInput
            className={`${height === 'thin' ? '!h-12' : '!h-16'}`}
            classNames={inputClassNames}
            name={inputName}
            placeholder={isPlaceholderArray ? '' : displayPlaceholder}
            startContent={IconComponent}
            type={type === 'password' && isPassword ? 'text' : type}
            value={value}
            onCopy={handleCopy}
            onKeyDown={onKeyDown}
            onFocus={onFocus}
            maxLength={maxLength}
            {...register}
            onChange={handleChange}
            onBlur={handleBlur}
            endContent={IconComponent2}
          />
          {isPlaceholderArray && !inputValue && (
            <div
              className="absolute left-0 top-0 pointer-events-none flex items-center h-full z-50"
              style={{
                left: IconComponent ? '2.5rem' : '0.75rem',
              }}
            >
              <span className={`text-sm ${theme === 'light' ? 'text-hbackground/60' : '!text-default-500'}`}>
                {placeholderContent}
              </span>
            </div>
          )}
        </div>
      </div>
      {errorMessage && (
        <p className="!text-danger-500 text-xs font-text">{errorMessage}</p>
      )}
    </div>
  );
};
